import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import { showSuccess, showError } from '../utils/toast';
import './UserModal.css';

const PlanModal = ({ plan, onClose, onPlanUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    weekly_tokens_remaining: 0,
    monthly_tokens_remaining: 0,
    max_avatars: 0,
    max_storage_gb: 0,
    max_memory_chunks: 0,
    free_trial_days: 0,
    is_active: true,
    pricing_options: [
      { plan_interval: 'monthly', price_usd: 0 },
      { plan_interval: 'yearly', price_usd: 0 }
    ],
    enabled_intervals: {
      monthly: false,
      yearly: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [fetchingPlan, setFetchingPlan] = useState(false);
  const [error, setError] = useState('');
  const [planData, setPlanData] = useState(null);

  // Use plan data directly from list (which should already have pricing_options)
  // Don't fetch individual plan details as GET /admin/plans/{id} returns 405 Method Not Allowed
  useEffect(() => {
    if (plan) {
      console.log('Plan object received in PlanModal:', plan);
      console.log('Plan ID:', plan.id);
      console.log('Plan name:', plan.name);
      console.log('pricing_options in plan:', plan.pricing_options);
      console.log('Full plan object keys:', Object.keys(plan));

      // Use plan data directly - it should already contain pricing_options from the list API
      setPlanData(plan);
      setFetchingPlan(false);
      setError(''); // Clear any previous errors
    } else {
      setPlanData(null);
      setFetchingPlan(false);
    }
  }, [plan]);

  // Update form data when planData changes
  useEffect(() => {
    if (planData) {
      // Handle existing plan - check if it has pricing_options or old structure
      let pricingOptions = [];

      console.log('Setting form data from planData:', planData);
      console.log('pricing_options in planData:', planData.pricing_options);

      // Determine which intervals are enabled from the original API response
      let enabledIntervals = {
        monthly: false,
        yearly: false
      };

      if (planData.pricing_options && Array.isArray(planData.pricing_options) && planData.pricing_options.length > 0) {
        console.log('Found pricing_options in planData, mapping them...');
        // Determine enabled intervals from original API response
        enabledIntervals = {
          monthly: planData.pricing_options.some(opt => opt.plan_interval === 'monthly'),
          yearly: planData.pricing_options.some(opt => opt.plan_interval === 'yearly')
        };

        pricingOptions = planData.pricing_options.map(opt => {
          console.log('Mapping option:', opt);
          const mapped = {
            plan_interval: opt.plan_interval,
            price_usd: parseFloat(opt.price_usd) || 0
          };
          console.log('Mapped option result:', mapped);
          return mapped;
        });
        console.log('Final mapped pricing_options:', pricingOptions);
      } else {
        console.log('No pricing_options found in planData, using fallback');
        // Fallback: create pricing_options from old structure if needed
        pricingOptions = [
          { plan_interval: 'monthly', price_usd: planData.price_usd || 0 },
          { plan_interval: 'yearly', price_usd: (planData.price_usd || 0) * 12 }
        ];
        // If using fallback, enable all intervals
        enabledIntervals = {
          monthly: true,
          yearly: true
        };
      }

      // Ensure all three pricing options exist in the form (for UI)
      const requiredIntervals = ['monthly', 'yearly'];
      requiredIntervals.forEach(interval => {
        const exists = pricingOptions.find(opt => opt.plan_interval === interval);
        if (!exists) {
          pricingOptions.push({ plan_interval: interval, price_usd: 0 });
        }
      });

      setFormData({
        name: planData.name || '',
        weekly_tokens_remaining: planData.weekly_tokens_remaining || 0,
        monthly_tokens_remaining: planData.monthly_tokens_remaining || 0,
        max_avatars: planData.max_avatars || 0,
        max_storage_gb: planData.max_storage_gb || 0,
        max_memory_chunks: planData.max_memory_chunks || 0,
        free_trial_days: planData.free_trial_days || 0,
        is_active: planData.is_active !== undefined ? planData.is_active : true,
        pricing_options: pricingOptions,
        enabled_intervals: enabledIntervals
      });

      console.log('Form data set:', {
        name: planData.name || '',
        pricing_options: pricingOptions
      });
    } else {
      // Reset form when no plan (new plan mode)
      setFormData({
        name: '',
        weekly_tokens_remaining: 0,
        monthly_tokens_remaining: 0,
        max_avatars: 0,
        max_storage_gb: 0,
        max_memory_chunks: 0,
        free_trial_days: 0,
        is_active: true,
        pricing_options: [
          { plan_interval: 'monthly', price_usd: 0 },
          { plan_interval: 'yearly', price_usd: 0 }
        ],
        enabled_intervals: {
          monthly: false,
          yearly: false
        }
      });
    }
  }, [planData]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    let finalValue;

    if (type === 'checkbox') {
      finalValue = checked;
    } else if (type === 'number') {
      // Allow empty string so user can clear the input
      if (value === '') {
        finalValue = '';
      } else {
        const parsed = parseFloat(value);
        finalValue = isNaN(parsed) ? 0 : parsed;
      }
    } else {
      finalValue = value;
    }

    setFormData({
      ...formData,
      [name]: finalValue
    });
  };

  const handlePricingOptionChange = (interval, field, value) => {
    const updatedOptions = [...formData.pricing_options];
    let finalValue;
    if (field === 'price_usd') {
      // Handle 0 correctly - parseFloat('0') is 0, not falsy
      if (value === '') {
        finalValue = '';
      } else {
        const parsed = parseFloat(value);
        finalValue = isNaN(parsed) ? '' : parsed;
      }
    } else {
      finalValue = value;
    }

    // Find the option by interval instead of index
    const optionIndex = updatedOptions.findIndex(opt => opt.plan_interval === interval);
    if (optionIndex !== -1) {
      // Update existing option
      const existingOption = updatedOptions[optionIndex];
      updatedOptions[optionIndex] = {
        ...existingOption,
        [field]: finalValue
      };
    } else {
      // If option doesn't exist, create it with all required fields
      const newOption = { plan_interval: interval };
      newOption.price_usd = finalValue === '' ? 0 : finalValue;
      updatedOptions.push(newOption);
    }

    setFormData({
      ...formData,
      pricing_options: updatedOptions
    });
  };

  const handleToggleInterval = (interval) => {
    const newEnabledState = !formData.enabled_intervals[interval];
    const updatedIntervals = { ...formData.enabled_intervals };

    updatedIntervals[interval] = newEnabledState;

    if (newEnabledState) {
      if (interval === 'monthly') {
        updatedIntervals.yearly = false;
      } else if (interval === 'yearly') {
        updatedIntervals.monthly = false;
      }
    }

    setFormData({
      ...formData,
      enabled_intervals: updatedIntervals
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate that at least one pricing option is enabled
      const hasEnabledInterval = Object.values(formData.enabled_intervals).some(enabled => enabled);
      if (!hasEnabledInterval) {
        setError('Please select a pricing option (Monthly or Yearly)');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');

      // Prepare plan data (without pricing_options for updates)
      const planDataPayload = {
        name: formData.name.trim(),
        weekly_tokens_remaining: parseInt(formData.weekly_tokens_remaining) || 0,
        monthly_tokens_remaining: parseInt(formData.monthly_tokens_remaining) || 0,
        is_active: Boolean(formData.is_active),
        max_avatars: parseInt(formData.max_avatars) || 0,
        max_storage_gb: parseInt(formData.max_storage_gb) || 0,
        max_memory_chunks: parseInt(formData.max_memory_chunks) || 0,
        free_trial_days: parseInt(formData.free_trial_days) || 0
      };

      // Prepare pricing_options - only send enabled intervals
      // Format according to API structure:
      // monthly: { "plan_interval": "monthly", "price_usd": 19.99 }
      // yearly: { "plan_interval": "yearly", "price_usd": 199.00 }
      let pricingOptionsPayload = [];
      if (formData.pricing_options && Array.isArray(formData.pricing_options) && formData.pricing_options.length > 0) {
        pricingOptionsPayload = formData.pricing_options
          .filter(option => formData.enabled_intervals[option.plan_interval])
          .map(option => {
            return {
              plan_interval: option.plan_interval,
              price_usd: parseFloat(option.price_usd) || 0
            };
          });
      }

      console.log('Submitting plan data:', planDataPayload);
      console.log('Submitting pricing options:', pricingOptionsPayload);
      console.log('Plan ID:', planData?.id || plan?.id);

      let response;
      const planId = planData?.id || plan?.id;
      if (planId) {
        // Update existing plan - send all properties including pricing_options
        const planEndpoint = `/admin/plans/${planId}`;
        console.log('PUT Request to (plan details):', planEndpoint);

        const updatePayload = {
          ...planDataPayload,
          pricing_options: pricingOptionsPayload
        };

        response = await apiClient.put(planEndpoint, updatePayload, token);
      } else {
        // Create new plan - use POST /admin/plans (with pricing_options included)
        const createData = {
          ...planDataPayload,
          pricing_options: pricingOptionsPayload
        };
        response = await apiClient.post('/admin/plans', createData, token);
      }

      // Wait a moment to ensure the update is processed on the server
      await new Promise(resolve => setTimeout(resolve, 100));

      showSuccess((planData?.id || plan?.id) ? 'Plan updated successfully' : 'Plan created successfully');

      // Pass server-returned plan back to parent when available (includes updated pricing)
      if (planData?.id || plan?.id) {
        const serverPlan = response?.data || response;
        const updatedPlan = serverPlan && typeof serverPlan === 'object' && serverPlan.id
          ? serverPlan
          : {
              ...planData,
              ...planDataPayload,
              pricing_options: pricingOptionsPayload,
              is_active: Boolean(formData.is_active),
              id: planData?.id || plan?.id
            };
        if (onPlanUpdated) {
          onPlanUpdated(updatedPlan);
        }
      }

      onClose();
    } catch (err) {
      console.error('Error saving plan:', err);

      // Extract detailed error message from API response
      let errorMessage = 'Failed to save plan';

      // Helper function to safely extract string from error
      const extractString = (value) => {
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value !== null) {
          // Handle objects with code and message
          if (value.message && typeof value.message === 'string') return value.message;
          if (value.msg && typeof value.msg === 'string') return value.msg;
          if (value.error && typeof value.error === 'string') return value.error;
          // Last resort: stringify the object
          return JSON.stringify(value);
        }
        return String(value);
      };

      if (err.data) {
        // Handle validation errors from API
        if (Array.isArray(err.data.detail)) {
          const validationErrors = err.data.detail.map(e => {
            const field = e.loc ? e.loc.join('.') : 'field';
            const msg = extractString(e.msg);
            return `${field}: ${msg}`;
          }).join(', ');
          errorMessage = `Validation error: ${validationErrors}`;
        } else if (err.data.detail) {
          errorMessage = extractString(err.data.detail);
        } else if (err.data.message) {
          errorMessage = extractString(err.data.message);
        } else if (err.data.error) {
          errorMessage = extractString(err.data.error);
        } else if (err.data.code && err.data.message) {
          // Handle {code, message} structure
          errorMessage = extractString(err.data.message);
        }
      } else if (err.error) {
        errorMessage = extractString(err.error);
      } else if (err.message) {
        errorMessage = extractString(err.message);
      }

      // Ensure errorMessage is always a string
      if (typeof errorMessage !== 'string') {
        errorMessage = String(errorMessage);
      }

      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{(planData?.id || plan?.id) ? 'Edit Plan' : 'Add New Plan'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && !error.includes('Warning') && <div className="error-message">{String(error)}</div>}

        <form onSubmit={handleSubmit}>
          {/* Helper constant for edit mode */}
          {(() => {
            const isEditMode = !!(planData?.id || plan?.id);
            return (
              <>
                <div className="form-group">
                  <label>Plan Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Free, Personal, Family"
                  />
                </div>



                <div className="form-group-row">
                  <div className="form-group">
                    <label>Weekly Tokens *</label>
                    <input
                      type="number"
                      min="0"
                      name="weekly_tokens_remaining"
                      value={formData.weekly_tokens_remaining}
                      onChange={handleChange}
                      required
                      placeholder="e.g., 250"
                    />
                  </div>
                  <div className="form-group">
                    <label>Monthly Tokens *</label>
                    <input
                      type="number"
                      min="0"
                      name="monthly_tokens_remaining"
                      value={formData.monthly_tokens_remaining}
                      onChange={handleChange}
                      required
                      placeholder="e.g., 1000"
                    />
                  </div>
                </div>
                <small className="form-help-text" style={{ marginBottom: '15px', display: 'block' }}>Tokens included in this plan (Weekly and Monthly limits)</small>

                <div className="form-group">
                  <label>Pricing Options *</label>
                  <div className="pricing-options-container">
                    {/* Monthly Pricing */}
                    <div className="pricing-option-row">
                      <label className="active-plan-label" style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={formData.enabled_intervals.monthly}
                          onChange={() => handleToggleInterval('monthly')}
                          className="active-plan-checkbox"
                        />
                        <span className="pricing-label">Monthly</span>
                      </label>
                      {formData.enabled_intervals.monthly && (
                        <>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={(formData.pricing_options.find(opt => opt.plan_interval === 'monthly')?.price_usd ?? 0).toString()}
                            onChange={(e) => {
                              handlePricingOptionChange('monthly', 'price_usd', e.target.value);
                            }}
                            placeholder="0.00"
                            className="pricing-input"
                          />
                          <span className="pricing-currency">USD</span>
                        </>
                      )}
                    </div>

                    {/* Yearly Pricing */}
                    <div className="pricing-option-row">
                      <label className="active-plan-label" style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={formData.enabled_intervals.yearly}
                          onChange={() => handleToggleInterval('yearly')}
                          className="active-plan-checkbox"
                        />
                        <span className="pricing-label">Yearly</span>
                      </label>
                      {formData.enabled_intervals.yearly && (
                        <>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={(formData.pricing_options.find(opt => opt.plan_interval === 'yearly')?.price_usd ?? 0).toString()}
                            onChange={(e) => {
                              handlePricingOptionChange('yearly', 'price_usd', e.target.value);
                            }}
                            placeholder="0.00"
                            className="pricing-input"
                          />
                          <span className="pricing-currency">USD</span>
                        </>
                      )}
                    </div>

                  </div>
                  <small className="pricing-help-text">
                    Select a pricing interval: Monthly or Yearly
                  </small>
                </div>

                <div className="form-group">
                  <label>Max Avatars *</label>
                  <input
                    type="number"
                    min="0"
                    name="max_avatars"
                    value={formData.max_avatars}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Max Storage (GB) *</label>
                  <input
                    type="number"
                    min="0"
                    name="max_storage_gb"
                    value={formData.max_storage_gb}
                    onChange={handleChange}
                    required
                  />
                </div>


                <div className="form-group">
                  <label>Max Memory Chunks *</label>
                  <input
                    type="number"
                    min="0"
                    name="max_memory_chunks"
                    value={formData.max_memory_chunks}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Free Plan Days</label>
                  <input
                    type="number"
                    min="0"
                    name="free_trial_days"
                    value={formData.free_trial_days}
                    onChange={handleChange}
                  />
                  <small className="form-help-text">Number of days this free plan is valid for (0 for unlimited)</small>
                </div>

                <div className="form-group">
                  <label className="active-plan-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      className="active-plan-checkbox"
                    />
                    {' '}Active Plan
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={onClose}>
                    Cancel
                  </button>
                  <button type="submit" className="save-btn" disabled={loading || fetchingPlan}>
                    {loading ? 'Saving...' : (planData?.id || plan?.id) ? 'Update' : 'Create'}
                  </button>
                </div>
              </>
            );
          })()}
        </form>

      </div>
    </div>
  );
};

export default PlanModal;
